import type { UseQueryResult } from '@tanstack/react-query';
import type { ResolvedUserConfig, UserConfig } from '../types/userConfig';
import { mergeUserConfig } from './userConfigModel';

export type UserConfigStatus = 'waiting-for-auth' | 'loading' | 'ready';

export type UserConfigSessionKey = 'auth';

export function getUserConfigSessionKey(
  isLoading: boolean,
  isAuthenticated: boolean,
): UserConfigSessionKey | null {
  if (isLoading || !isAuthenticated) {
    return null;
  }

  return 'auth';
}

export function getUserConfigStatus(
  sessionKey: UserConfigSessionKey | null,
  configQuery: Pick<UseQueryResult<UserConfig>, 'isPending'>,
): UserConfigStatus {
  if (sessionKey === null) {
    return 'waiting-for-auth';
  }

  // Only the initial fetch — background refetches after updates must not
  // flip isUserConfigReady back to false (e.g. pinning a knowledge database).
  if (configQuery.isPending) {
    return 'loading';
  }

  return 'ready';
}

export function mergeRemoteUserConfig(
  current: ResolvedUserConfig,
  partial: UserConfig,
): ResolvedUserConfig {
  return mergeUserConfig(current, {
    ...partial,
    knowledgeDocumentsPageSize:
      partial.knowledgeDocumentsPageSize ?? current.knowledgeDocumentsPageSize,
    galleryImagesPageSize:
      partial.galleryImagesPageSize ?? current.galleryImagesPageSize,
    chatbotConversationsPageSize:
      partial.chatbotConversationsPageSize ??
      current.chatbotConversationsPageSize,
    chatbotTrainingExamplesPageSize:
      partial.chatbotTrainingExamplesPageSize ??
      current.chatbotTrainingExamplesPageSize,
    pinnedKnowledgeDatabaseIds:
      partial.pinnedKnowledgeDatabaseIds !== undefined
        ? partial.pinnedKnowledgeDatabaseIds
        : current.pinnedKnowledgeDatabaseIds,
    platformChatLastSeenMessageIds:
      partial.platformChatLastSeenMessageIds !== undefined
        ? partial.platformChatLastSeenMessageIds
        : current.platformChatLastSeenMessageIds,
  });
}
